import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertCircle, AlertTriangle, ChevronRight, X } from "lucide-react";

import {
    FORM_STEPPER_HEIGHT_VAR,
    VALIDATION_BANNER_ATTR,
    getStickyPageScrollOffset,
    scrollElementBelowStickyHeader,
} from "@/utils/stickyScroll";

/**
 * Where the banner parks once it sticks: under the fixed page header and, on
 * form pages, under the section stepper docked below it. `--form-stepper-height`
 * is 0 while that dock is hidden, so the banner rides up and down with it
 * instead of ever sharing the same band.
 */
const BANNER_PAGE_STICKY_TOP = `calc(var(--page-header-height, 64px) + var(${FORM_STEPPER_HEIGHT_VAR}, 0px) + 8px)`;

/**
 * Inside a modal the page chrome is irrelevant — the banner sticks to the top of
 * the dialog's own scroll area.
 */
const BANNER_OVERLAY_STICKY_TOP = "0px";

/** True when the banner is rendered inside a fixed overlay (modal / drawer). */
function hasFixedAncestor(node) {
    let el = node?.parentElement || null;
    while (el && el !== document.body) {
        if (window.getComputedStyle(el).position === "fixed") return true;
        el = el.parentElement;
    }
    return false;
}

const VALIDATION_ERROR_CLASS = "validation-error-control";
const VALIDATION_STYLE_ID = "shipez-form-validation-styles";
let validationListenersInstalled = false;

function ensureValidationStyles() {
    if (typeof document === "undefined") return;
    if (document.getElementById(VALIDATION_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = VALIDATION_STYLE_ID;
    style.textContent = `
        .${VALIDATION_ERROR_CLASS} {
            border-color: rgb(239 68 68) !important;
        }
        .field-validation-flash {
            animation: field-validation-pulse 1.1s ease-out;
        }
        @keyframes field-validation-pulse {
            0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.45); }
            70% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
    `;
    document.head.appendChild(style);
}

function installValidationClearListeners() {
    if (typeof document === "undefined" || validationListenersInstalled) return;
    validationListenersInstalled = true;

    const clearTarget = (event) => {
        const target = event.target;
        if (!target?.classList?.contains?.(VALIDATION_ERROR_CLASS)) return;
        target.classList.remove(VALIDATION_ERROR_CLASS);
        target.closest?.("[data-field]")?.classList?.remove?.(VALIDATION_ERROR_CLASS);
    };

    document.addEventListener("input", clearTarget, true);
    document.addEventListener("change", clearTarget, true);
}

function escapeAttr(value) {
    if (typeof CSS !== "undefined" && CSS.escape) return CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
}

function resolveFieldElements(fieldKey) {
    const escaped = escapeAttr(fieldKey);
    const nodes = [
        ...document.querySelectorAll(`[data-field="${escaped}"]`),
        ...document.querySelectorAll(`[name="${escaped}"]`),
    ];

    const controls = [];
    nodes.forEach((node) => {
        if (node.matches?.("input, select, textarea, button")) {
            controls.push(node);
            return;
        }
        const nested = node.querySelector?.("input, select, textarea, button");
        controls.push(nested || node);
    });

    return [...new Set(controls.filter(Boolean))];
}

/** Remove previous red-border highlights. */
export function clearValidationFieldHighlights() {
    if (typeof document === "undefined") return;
    document
        .querySelectorAll(`.${VALIDATION_ERROR_CLASS}`)
        .forEach((el) => el.classList.remove(VALIDATION_ERROR_CLASS));
}

/** Apply red-border highlight to matching named / data-field controls. */
export function applyValidationFieldHighlights(errors = {}) {
    if (typeof document === "undefined") return;
    ensureValidationStyles();
    installValidationClearListeners();
    clearValidationFieldHighlights();

    Object.keys(errors)
        .filter((key) => Boolean(errors[key]))
        .forEach((key) => {
            resolveFieldElements(key).forEach((el) => {
                el.classList.add(VALIDATION_ERROR_CLASS);
            });
        });
}

/**
 * Highlight + scroll/focus first invalid field.
 * Safe to call from any form after validateForm.
 */
export function presentValidationErrors(errors = {}, { scroll = true } = {}) {
    const active = Object.fromEntries(
        Object.entries(errors || {}).filter(([, msg]) => Boolean(msg)),
    );
    if (!Object.keys(active).length) {
        clearValidationFieldHighlights();
        return null;
    }

    applyValidationFieldHighlights(active);

    if (!scroll) return null;

    return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
            window.setTimeout(() => {
                resolve(focusAndScrollToFirstError(active));
            }, 60);
        });
    });
}

/**
 * Validates form data and line items against provided rules
 * @returns {Object} Object containing field error messages
 */
export const validateForm = ({
    data = {},
    items = [],
    rules = {},
    showToast = true,
    autoFocus = true,
}) => {
    const errors = {};

    if (!rules || typeof rules !== "object") {
        console.warn("validateForm: No validation rules provided");
        return errors;
    }

    Object.keys(rules).forEach((field) => {
        if (field === "_items") return;

        const rule = rules[field];
        if (!rule || typeof rule !== "object") return;

        if (rule.required) {
            const value = data?.[field];
            const isInvalid =
                value === undefined ||
                value === null ||
                value === false ||
                (typeof value === "string" && value.trim() === "") ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === "number" && (isNaN(value) || value === 0));

            if (isInvalid) {
                errors[field] = rule?.message || `${rule?.label} is required`;
            }
        }
    });

    if (items.length > 0 && rules._items && typeof rules._items === "object") {
        const itemRules = rules._items;

        items.forEach((item, index) => {
            Object.keys(itemRules).forEach((field) => {
                if (field === "_items") return;

                const rule = itemRules[field];
                if (!rule || typeof rule !== "object") return;

                if (rule.required) {
                    const value = item[field];
                    const isInvalid =
                        value === undefined ||
                        value === null ||
                        (typeof value === "string" && value.trim() === "") ||
                        (typeof value === "number" &&
                            (isNaN(value) || value <= 0));

                    if (isInvalid) {
                        errors[`${field}_${index}`] =
                            `${rule.message} is required in row ${index + 1}` ||
                            `${field} is required for item ${index + 1}`;
                    }
                }
            });
        });
    }

    const errorMessages = Object.values(errors).map((errFn, idx) =>
        typeof errFn === "function" ? errFn(idx) : errFn,
    );

    if (errorMessages.length > 0) {
        if (showToast) {
            toast.error(
                `Please complete ${errorMessages.length} required field${errorMessages.length === 1 ? "" : "s"}`,
                {
                    description:
                        "Missing fields are highlighted — jump to the first one.",
                    duration: 4500,
                    closeButton: true,
                },
            );
        }
        if (autoFocus) {
            presentValidationErrors(errors);
        }
    } else {
        clearValidationFieldHighlights();
    }

    return errors;
};

/** Tailwind classes for controls with validation errors (border only). */
export const getControlErrorClass = (hasError) =>
    hasError ? "border-red-500 dark:border-red-500 focus:border-red-500" : "";

function getDefaultStickyOffset() {
    // getStickyPageScrollOffset already reserves the pinned validation banner.
    return getStickyPageScrollOffset(16);
}

function scrollElementIntoViewCentered(el, offsetTop = getDefaultStickyOffset()) {
    if (!el) return;
    // Prefer #main-content scroll with sticky header offset (Import Booking pattern).
    scrollElementBelowStickyHeader(el, { offsetTop, behavior: "smooth" });
}

/**
 * Scroll to and focus the first invalid field (DOM order).
 * Looks for `[data-field]` wrappers first, then `[name]`.
 */
export function focusAndScrollToFirstError(errors = {}, options = {}) {
    ensureValidationStyles();
    installValidationClearListeners();

    const keys = Object.keys(errors).filter((key) => Boolean(errors[key]));
    if (!keys.length) return null;

    const offsetTop =
        typeof options.offsetTop === "number"
            ? options.offsetTop
            : getDefaultStickyOffset();

    const findCandidates = () =>
        keys
            .map((key) => {
                const els = resolveFieldElements(key);
                const el = els[0];
                return el ? { key, el } : null;
            })
            .filter(Boolean)
            .sort((a, b) => {
                const pos = a.el.compareDocumentPosition(b.el);
                if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
                if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
                return 0;
            });

    let candidates = findCandidates();

    // If nothing found yet (e.g. section still expanding), retry shortly.
    if (!candidates.length) {
        window.setTimeout(() => {
            candidates = findCandidates();
            const retry = candidates[0];
            if (!retry) return;
            applyValidationFieldHighlights({ [retry.key]: errors[retry.key] });
            scrollElementIntoViewCentered(retry.el, offsetTop);
            window.setTimeout(() => {
                const focusable = retry.el.matches(
                    "input, select, textarea, button",
                )
                    ? retry.el
                    : retry.el.querySelector("input, select, textarea, button");
                focusable?.focus?.({ preventScroll: true });
                retry.el.classList.add("field-validation-flash");
                window.setTimeout(
                    () => retry.el.classList.remove("field-validation-flash"),
                    1200,
                );
            }, 200);
        }, options.retryDelayMs ?? 120);
        return null;
    }

    const first = candidates[0];
    applyValidationFieldHighlights({ [first.key]: errors[first.key] });
    scrollElementIntoViewCentered(first.el, offsetTop);

    window.setTimeout(() => {
        const focusable = first.el.matches("input, select, textarea, button")
            ? first.el
            : first.el.querySelector("input, select, textarea, button");
        focusable?.focus?.({ preventScroll: true });
        first.el.classList.add("field-validation-flash");
        window.setTimeout(() => {
            first.el.classList.remove("field-validation-flash");
        }, 1200);
    }, 280);

    return first.key;
}

/** Compact inline error under a field. */
export function FieldErrorMessage({ message }) {
    if (!message) return null;
    return (
        <p
            role="alert"
            className="mt-1.5 flex items-start gap-1.5 text-xs font-medium text-red-600 dark:text-red-400"
        >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{message}</span>
        </p>
    );
}

/**
 * Modern sticky validation summary with jump-to-field chips.
 */
export function FormValidationBanner({
    errors = {},
    onJumpToField,
    onDismiss,
}) {
    const entries = Object.entries(errors).filter(([, msg]) => Boolean(msg));

    return (
        <BannerShell hidden={!entries.length}>
            <BannerBody
                entries={entries}
                onJumpToField={onJumpToField}
                onDismiss={onDismiss}
            />
        </BannerShell>
    );
}

/**
 * Sticky wrapper. Kept separate from the content so the sticky offset can be
 * resolved from the mounted node (page chrome vs. modal overlay).
 * `z-30` keeps it under the stepper dock (`z-40`) and the header (`z-50`), so the
 * three never trade places depending on which page renders them in which order.
 */
function BannerShell({ hidden, children }) {
    const ref = useRef(null);
    const [inOverlay, setInOverlay] = useState(false);

    useEffect(() => {
        if (hidden) return;
        setInOverlay(hasFixedAncestor(ref.current));
    }, [hidden]);

    if (hidden) return null;

    return (
        <div
            ref={ref}
            role="alert"
            aria-live="polite"
            {...{ [VALIDATION_BANNER_ATTR]: "true" }}
            style={{
                top: inOverlay
                    ? BANNER_OVERLAY_STICKY_TOP
                    : BANNER_PAGE_STICKY_TOP,
            }}
            className="sticky z-30 mb-4 overflow-hidden rounded-xl border border-red-200/90 bg-gradient-to-br from-red-50 via-white to-amber-50/60 shadow-[0_8px_30px_rgba(185,28,28,0.12)] transition-[top] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] dark:border-red-900/60 dark:from-red-950/80 dark:via-slate-900 dark:to-slate-900"
        >
            {children}
        </div>
    );
}

function BannerBody({ entries, onJumpToField, onDismiss }) {
    return (
        <div className="flex items-start gap-3 px-4 py-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-sm shadow-red-500/30">
                <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                            {entries.length} field
                            {entries.length === 1 ? "" : "s"} need
                            {entries.length === 1 ? "s" : ""} attention
                        </p>
                        <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
                            Click a field below to jump there, or scroll to the
                            highlighted inputs.
                        </p>
                    </div>
                    {onDismiss ? (
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="rounded-md p-1 text-red-500/80 transition hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-900/40"
                            aria-label="Dismiss validation summary"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    ) : null}
                </div>

                <ul className="mt-3 flex flex-wrap gap-2">
                    {entries.map(([field, message]) => (
                        <li key={field}>
                            <button
                                type="button"
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (onJumpToField) {
                                        onJumpToField(field);
                                        // Ensure scroll still runs after parent expands sections
                                        window.setTimeout(() => {
                                            focusAndScrollToFirstError({
                                                [field]: message,
                                            });
                                        }, 140);
                                        return;
                                    }
                                    focusAndScrollToFirstError({
                                        [field]: message,
                                    });
                                }}
                                className="group inline-flex max-w-full items-center gap-1.5 rounded-full border border-red-200 bg-white/90 px-2.5 py-1 text-left text-xs font-medium text-red-700 shadow-sm transition hover:border-red-400 hover:bg-red-50 dark:border-red-800 dark:bg-slate-900/80 dark:text-red-300 dark:hover:bg-red-950/50"
                            >
                                <span className="truncate">{message}</span>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}

/**
 * Shared validation UX for any form:
 * - stores errors
 * - sticky banner
 * - red-border highlights + scroll (also triggered by validateForm)
 */
export function useValidatedForm() {
    const [errors, setErrors] = useState({});

    useEffect(() => {
        ensureValidationStyles();
        installValidationClearListeners();
    }, []);

    const clearErrors = useCallback(() => {
        setErrors({});
        clearValidationFieldHighlights();
    }, []);

    const applyErrors = useCallback((nextErrors = {}, options = {}) => {
        const active = Object.fromEntries(
            Object.entries(nextErrors || {}).filter(([, msg]) => Boolean(msg)),
        );
        setErrors(active);

        if (!Object.keys(active).length) {
            clearValidationFieldHighlights();
            return false;
        }

        // validateForm may already have highlighted; re-present keeps banner jumps in sync
        if (options.skipPresent !== true) {
            presentValidationErrors(active, { scroll: options.scroll !== false });
        }
        return true;
    }, []);

    const jumpToField = useCallback(
        (field) => {
            if (!field || !errors?.[field]) return;
            focusAndScrollToFirstError({ [field]: errors[field] });
        },
        [errors],
    );

    const controlErrorClass = useCallback(
        (field) => getControlErrorClass(Boolean(errors?.[field])),
        [errors],
    );

    const clearFieldError = useCallback((fieldName) => {
        if (!fieldName) return;
        setErrors((prev) => {
            if (!prev?.[fieldName]) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
        resolveFieldElements(fieldName).forEach((el) => {
            el.classList.remove(VALIDATION_ERROR_CLASS);
        });
    }, []);

    const ValidationBanner = useMemo(() => {
        function Banner(props) {
            return (
                <FormValidationBanner
                    errors={errors}
                    onJumpToField={jumpToField}
                    onDismiss={clearErrors}
                    {...props}
                />
            );
        }
        return Banner;
    }, [errors, jumpToField, clearErrors]);

    return {
        errors,
        setErrors,
        applyErrors,
        clearErrors,
        clearFieldError,
        jumpToField,
        controlErrorClass,
        ValidationBanner,
        FieldError: FieldErrorMessage,
        hasErrors: Object.keys(errors).some((key) => Boolean(errors[key])),
    };
}
